import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AlertCircle,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock3,
  FileText,
  Loader2,
  Plus,
  Send,
  Stethoscope,
  TimerReset,
  WalletCards,
  XCircle,
} from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import {
  computeAutoLeaveCompensation,
  computeMonthAttendanceStats,
  normalizeLeaveType,
} from "../../utils/calendarAttendance";
import { MIN_TEXT_WORDS, getMinimumWordsError } from "../../utils/textValidation";

const leaveTypes = [
  { value: "casual", label: "Casual Leave", short: "CL", balanceKey: "casualLeave", icon: CalendarDays },
  { value: "sick", label: "Sick / Medical", short: "ML", balanceKey: "medicalLeave", icon: Stethoscope },
  { value: "compensatory", label: "Compensatory Off", short: "CO", balanceKey: "compensatoryLeave", icon: TimerReset },
  { value: "lop", label: "Loss of Pay", short: "LOP", balanceKey: "lossOfPay", icon: FileText, unlimited: true },
];

const requestableLeaveTypes = leaveTypes.filter((type) => type.requestable !== false);

const today = new Date().toISOString().split("T")[0];
const toMonthKey = (date = new Date()) => {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthRange = (date = new Date()) => {
  const value = new Date(date);
  const start = new Date(value.getFullYear(), value.getMonth(), 1);
  const end = new Date(value.getFullYear(), value.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
};

const addMonths = (date, delta) => new Date(date.getFullYear(), date.getMonth() + delta, 1);

const formatMonthLabel = (date) => new Date(date).toLocaleDateString("en-IN", {
  month: "long",
  year: "numeric",
});

const getLeaveCacheKey = (employee) => {
  const id = employee?._id || employee?.id;
  return id ? `employee_leave_history_${id}` : null;
};

const readLeaveCache = (employee) => {
  const cacheKey = getLeaveCacheKey(employee);
  if (!cacheKey) return [];

  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
    if (!cached?.data) return [];
    return Array.isArray(cached.data) ? cached.data : [];
  } catch {
    localStorage.removeItem(cacheKey);
    return [];
  }
};

const writeLeaveCache = (employee, data) => {
  const cacheKey = getLeaveCacheKey(employee);
  if (!cacheKey) return;
  localStorage.setItem(cacheKey, JSON.stringify({ data, savedAt: Date.now() }));
};

const readAnyLeaveCache = () => {
  try {
    const keys = Object.keys(localStorage).filter((key) => key.startsWith("employee_leave_history_"));
    let latest = null;
    keys.forEach((key) => {
      const cached = JSON.parse(localStorage.getItem(key) || "null");
      if (!cached?.data || !Array.isArray(cached.data)) return;
      if (!latest || cached.savedAt > latest.savedAt) latest = cached;
    });
    return latest?.data || [];
  } catch {
    return [];
  }
};

const getLeaveBalance = (employee, leaveType) => {
  if (leaveType?.unlimited) return null;
  if (!leaveType?.balanceKey) return null;
  const baseBalance = Number(employee?.[leaveType.balanceKey] ?? 0);
  if (leaveType.value === "casual") {
    return baseBalance + Number(employee?.carriedForwardCasualLeave || 0);
  }
  if (leaveType.value === "sick") {
    return baseBalance + Number(employee?.carriedForwardMedicalLeave || 0);
  }
  return baseBalance;
};

const getLeaveDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diff = end - start;
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || diff < 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
};

const getApprovedLeaveUsage = (leaves = [], cutoffDate = new Date()) => {
  const cutoff = new Date(cutoffDate);
  cutoff.setHours(23, 59, 59, 999);
  return leaves.reduce(
    (usage, leave) => {
      if (leave.status !== "approved") return usage;
      const leaveStart = new Date(leave.startDate);
      if (Number.isNaN(leaveStart.getTime()) || leaveStart > cutoff) return usage;

      const days = getLeaveDays(leave.startDate, leave.endDate);
      if (!days) return usage;

      const allocation = leave.leaveAllocation || {};
      const casualAllocated = Number(allocation.carriedForwardCasualLeave || 0) + Number(allocation.casualLeave || 0);
      const medicalAllocated = Number(allocation.carriedForwardMedicalLeave || 0) + Number(allocation.medicalLeave || 0);
      const compensatoryAllocated = Number(allocation.compensatoryLeave || 0);
      const lopAllocated = Number(allocation.lossOfPay || 0);
      const hasAllocation = casualAllocated + medicalAllocated + compensatoryAllocated + lopAllocated > 0;

      if (hasAllocation) {
        usage.casual += casualAllocated;
        usage.medical += medicalAllocated;
        usage.compensatory += compensatoryAllocated;
        usage.lop += lopAllocated;
        return usage;
      }

      const leaveType = normalizeLeaveType(leave.leaveType);
      if (leaveType === "casual") usage.casual += days;
      if (leaveType === "sick" || leaveType === "medical" || leaveType === "emergency") usage.medical += days;
      if (leaveType === "compensatory") usage.compensatory += days;
      if (leaveType === "lop") usage.lop += days;

      return usage;
    },
    { casual: 0, medical: 0, compensatory: 0, lop: 0 },
  );
};

const doesLeaveOverlapRange = (leave, rangeStart, rangeEnd) => {
  const start = new Date(leave?.startDate);
  const end = new Date(leave?.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  return start <= rangeEnd && end >= rangeStart;
};

const filterLeavesForMonth = (leaves = [], monthDate = new Date()) => {
  const { start, end } = getMonthRange(monthDate);
  return leaves.filter((leave) => doesLeaveOverlapRange(leave, start, end));
};

const readAttendanceRecords = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.history)) return data.history;
  return [];
};

const getAutoUsageByType = (allocationByDate = {}) => {
  return Object.values(allocationByDate).reduce(
    (usage, value) => {
      if (value === "carryForwardCasual") usage.casual += 1;
      else if (value === "carryForwardMedical" || value === "sick") usage.medical += 1;
      else if (value === "casual") usage.casual += 1;
      else if (value === "compensatory") usage.compensatory += 1;
      else if (value === "lop") usage.lop += 1;
      return usage;
    },
    { casual: 0, medical: 0, compensatory: 0, lop: 0 },
  );
};

const getAutoLopCountForMonth = ({ employee, leaves = [], attendance = [], holidays = [], monthDate = new Date() }) => {
  const monthStats = computeMonthAttendanceStats(monthDate.getFullYear(), monthDate.getMonth(), {
    leaves,
    attendance,
    holidays,
    today: new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1),
    attendanceStartDate: employee?.attendanceStartDate || employee?.joiningDate,
  });
  const autoCompensation = computeAutoLeaveCompensation({ employee, dayMeta: monthStats.dayMeta });
  return Object.values(autoCompensation.allocationByDate || {}).filter((allocation) => allocation === "lop").length;
};

const getPriorMonthPaidLeaveUsage = (leaves = [], monthStart = new Date()) => {
  const previousMonthStart = new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1);
  return leaves.reduce(
    (usage, leave) => {
      if (leave.status !== "approved") return usage;

      const leaveEnd = new Date(leave.endDate);
      if (Number.isNaN(leaveEnd.getTime()) || leaveEnd < previousMonthStart || leaveEnd >= monthStart) return usage;

      const days = getLeaveDays(leave.startDate, leave.endDate);
      if (!days) return usage;

      const allocation = leave.leaveAllocation || {};
      const casualAllocated = Number(allocation.carriedForwardCasualLeave || 0) + Number(allocation.casualLeave || 0);
      const medicalAllocated = Number(allocation.carriedForwardMedicalLeave || 0) + Number(allocation.medicalLeave || 0);
      if (casualAllocated || medicalAllocated) {
        usage.casual += casualAllocated;
        usage.medical += medicalAllocated;
        return usage;
      }

      const leaveType = normalizeLeaveType(leave.leaveType);
      if (leaveType === "casual") usage.casual += days;
      if (leaveType === "sick" || leaveType === "medical" || leaveType === "emergency") usage.medical += days;
      return usage;
    },
    { casual: 0, medical: 0 },
  );
};

const isLeaveTypeDisabled = (employee, leaveType) => {
  if (!leaveType || leaveType.unlimited) return false;
  const balance = getLeaveBalance(employee, leaveType);
  return balance !== null && balance <= 0;
};

const getFirstAvailableLeaveType = (employee) => {
  return requestableLeaveTypes.find((type) => !isLeaveTypeDisabled(employee, type))?.value || "";
};

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const Leave = () => {
  const location = useLocation();
  const { employee, fetchEmployee } = useAuth();
  const cachedLeaves = useMemo(() => readLeaveCache(employee), [employee]);

  const [leaves, setLeaves] = useState(() => cachedLeaves.length ? cachedLeaves : readAnyLeaveCache());
  const [applyOpen, setApplyOpen] = useState(false);
  const [openLeaveId, setOpenLeaveId] = useState("");
  const [historyVisible, setHistoryVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitIndicator, setSubmitIndicator] = useState("");
  const [message, setMessage] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [form, setForm] = useState({
    startDate: "",
    endDate: "",
    selectedLeaveTypes: ["casual"],
    reason: "",
  });

  useEffect(() => {
    if (cachedLeaves.length) setLeaves(cachedLeaves);
  }, [cachedLeaves]);

  const fetchLeaves = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/leave/my", {
        params: { limit: 60 },
        cache: false
      });
      const nextLeaves = Array.isArray(res.data) ? res.data : [];
      setLeaves(nextLeaves);
      writeLeaveCache(employee, nextLeaves);
    } catch (err) {
      console.error("Failed to load leave history:", err);
    }
  }, [employee]);

  useEffect(() => {
    if (location.state?.openApplyLeave) {
      setApplyOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchLeaves();
    const timeoutId = window.setTimeout(() => {
      if (!document.hidden) fetchEmployee?.();
    }, 1200);
    
    // Poll every 10 seconds for instant status reflection
    const intervalId = window.setInterval(() => {
      fetchLeaves();
      if (!document.hidden) fetchEmployee?.();
    }, 10000);
    
    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [fetchEmployee, fetchLeaves]);

  useEffect(() => {
    let active = true;
    const fetchHolidays = async () => {
      try {
        const res = await api.get("/api/holidays/public");
        if (active) setHolidays(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (active) setHolidays([]);
      }
    };
    fetchHolidays();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const requestStamp = Date.now();
    const fetchAttendanceHistory = async () => {
      try {
        const res = await api.get("/api/employee/attendance/summary", {
          cache: false,
          params: { limit: 370, _t: requestStamp },
        });
        if (active) setAttendanceHistory(readAttendanceRecords(res.data));
      } catch {
        try {
          const res = await api.get("/api/employee/attendance/my", {
            cache: false,
            params: { limit: 370, _t: requestStamp },
          });
          if (active) setAttendanceHistory(readAttendanceRecords(res.data));
        } catch {
          if (active) setAttendanceHistory([]);
        }
      }
    };

    fetchAttendanceHistory();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!employee) return;
    setForm((current) => {
      const availableSelections = current.selectedLeaveTypes.filter((value) => {
        const type = leaveTypes.find((item) => item.value === value);
        return type && !isLeaveTypeDisabled(employee, type);
      });
      return availableSelections.length
        ? current
        : { ...current, selectedLeaveTypes: [getFirstAvailableLeaveType(employee)] };
    });
  }, [employee]);

  const selectedLeaveTypes = useMemo(
    () => leaveTypes.filter((type) => form.selectedLeaveTypes.includes(type.value)),
    [form.selectedLeaveTypes],
  );

  const requestedDays = useMemo(
    () => getLeaveDays(form.startDate, form.endDate),
    [form.startDate, form.endDate],
  );
  const selectedMonthLabel = useMemo(() => formatMonthLabel(selectedMonth), [selectedMonth]);
  const selectedMonthKey = useMemo(() => toMonthKey(selectedMonth), [selectedMonth]);
  const isCurrentMonthSelected = selectedMonthKey === toMonthKey(new Date());

  const stats = useMemo(() => {
    const monthLeaves = filterLeavesForMonth(leaves, selectedMonth);
    const { end: selectedMonthEnd } = getMonthRange(selectedMonth);
    const pending = leaves
      .filter((leave) => leave.status === "pending")
      .reduce((total, leave) => total + getLeaveDays(leave.startDate, leave.endDate), 0);
    const approved = leaves
      .filter((leave) => leave.status === "approved")
      .reduce((total, leave) => total + getLeaveDays(leave.startDate, leave.endDate), 0);
    const rejected = leaves
      .filter((leave) => leave.status === "rejected")
      .reduce((total, leave) => total + getLeaveDays(leave.startDate, leave.endDate), 0);
    const now = new Date();
    const effectiveCutoff = selectedMonthEnd < now ? selectedMonthEnd : now;
    // Approval is permission only. Balance usage is controlled manually by the admin.
    const usage = { casual: 0, medical: 0, compensatory: 0, lop: 0 };
    const monthlyCasualCredit = Number(employee?.monthlyCasualLeave ?? (String(employee?.role || employee?.employmentType || "").toLowerCase().includes("intern") ? 0 : 1));
    const monthlyMedicalCredit = Number(employee?.monthlyMedicalLeave ?? 1);
    const currentCasual = isCurrentMonthSelected
      ? Math.max(0, Number(employee?.casualLeave || 0))
      : monthlyCasualCredit;
    const currentMedical = isCurrentMonthSelected
      ? Math.max(0, Number(employee?.medicalLeave || 0))
      : monthlyMedicalCredit;
    const currentCompensatory = isCurrentMonthSelected ? Number(employee?.compensatoryLeave || 0) : 0;
    const effectiveCarryForwardCasual = isCurrentMonthSelected
      ? Math.max(0, Number(employee?.carriedForwardCasualLeave || 0))
      : 0;
    const effectiveCarryForwardMedical = isCurrentMonthSelected
      ? Math.max(0, Number(employee?.carriedForwardMedicalLeave || 0))
      : 0;
    // The current balances are entered and controlled by the admin. Always use
    // those saved values for the current month, including carry-forward, even
    // for older employee records that do not yet have the manual-policy flag.
    if (isCurrentMonthSelected || employee?.leavePolicyManualOverride) {
      const manualCasualBalance = currentCasual + effectiveCarryForwardCasual;
      const manualMedicalBalance = currentMedical + effectiveCarryForwardMedical;
      const manualTotal = manualCasualBalance + manualMedicalBalance + currentCompensatory;
      return {
        pending,
        approved,
        rejected,
        remainingBalance: manualTotal,
        totalAllocated: manualTotal,
        usedPaid: 0,
        carryForwardTotal: effectiveCarryForwardCasual + effectiveCarryForwardMedical,
        carryForwardByType: {
          casual: effectiveCarryForwardCasual,
          sick: effectiveCarryForwardMedical,
        },
        usage: { casual: 0, medical: 0, compensatory: 0, lop: 0 },
        balances: {
          casual: manualCasualBalance,
          sick: manualMedicalBalance,
          compensatory: currentCompensatory,
          lop: Number(employee?.lossOfPay || 0),
        },
      };
    }
    const effectiveEmployee = {
      ...employee,
      casualLeave: isCurrentMonthSelected ? currentCasual : monthlyCasualCredit,
      medicalLeave: isCurrentMonthSelected ? currentMedical : monthlyMedicalCredit,
      compensatoryLeave: 0,
      carriedForwardCasualLeave: effectiveCarryForwardCasual,
      carriedForwardMedicalLeave: effectiveCarryForwardMedical,
      monthlyCasualLeave: monthlyCasualCredit,
      monthlyMedicalLeave: monthlyMedicalCredit,
    };
    const monthStats = computeMonthAttendanceStats(selectedMonth.getFullYear(), selectedMonth.getMonth(), {
      leaves: monthLeaves,
      attendance: attendanceHistory,
      holidays,
      today: effectiveCutoff,
      attendanceStartDate: employee?.attendanceStartDate || employee?.joiningDate,
    });
    const autoCompensation = computeAutoLeaveCompensation({ employee: effectiveEmployee, dayMeta: monthStats.dayMeta });
    const autoUsage = getAutoUsageByType(autoCompensation.allocationByDate);
    const totalAutoPaidUsed = autoUsage.casual + autoUsage.medical + autoUsage.compensatory;
    const remainingCasual = Math.max(0, currentCasual - autoUsage.casual);
    const remainingMedical = Math.max(0, currentMedical - autoUsage.medical);
    const remainingCompensatory = Math.max(0, currentCompensatory - autoUsage.compensatory);
    const totalCasual = remainingCasual + usage.casual + autoUsage.casual;
    const totalMedical = remainingMedical + usage.medical + autoUsage.medical;
    const totalCompensatory = remainingCompensatory + usage.compensatory + autoUsage.compensatory;
    const totalAllocated = totalCasual + totalMedical + totalCompensatory;
    const usedPaid = usage.casual + usage.medical + usage.compensatory + totalAutoPaidUsed;
    const remainingBalance = remainingCasual + remainingMedical + remainingCompensatory;
    const carryForwardTotal =
      Math.min(effectiveCarryForwardCasual, remainingCasual) +
      Math.min(effectiveCarryForwardMedical, remainingMedical);
    const combinedUsage = {
      casual: usage.casual + autoUsage.casual,
      medical: usage.medical + autoUsage.medical,
      compensatory: usage.compensatory + autoUsage.compensatory,
      lop: usage.lop + autoUsage.lop,
    };

    return {
      pending,
      approved,
      rejected,
      remainingBalance,
      totalAllocated,
      usedPaid,
      carryForwardTotal,
      carryForwardByType: {
        casual: Math.min(effectiveCarryForwardCasual, remainingCasual),
        sick: Math.min(effectiveCarryForwardMedical, remainingMedical),
      },
      usage: combinedUsage,
      balances: {
        casual: remainingCasual,
        sick: remainingMedical,
        compensatory: remainingCompensatory,
        lop: combinedUsage.lop || Number(employee?.lossOfPay || 0),
      },
    };
  }, [attendanceHistory, employee, holidays, isCurrentMonthSelected, leaves, selectedMonth]);

  const requestDateSummary =
    form.startDate && form.endDate
      ? `${formatDate(form.startDate)} to ${formatDate(form.endDate)}`
      : "Choose dates";

  const closeApplyModal = () => {
    if (submitting) return;
    setApplyOpen(false);
    setMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!selectedLeaveTypes.length) {
      setMessage("Select at least one leave type.");
      return;
    }

    let remainingDays = requestedDays;
    const requestedAllocation = { casualLeave: 0, medicalLeave: 0, compensatoryLeave: 0, lossOfPay: 0 };
    const allocationFields = { casual: "casualLeave", sick: "medicalLeave", compensatory: "compensatoryLeave", lop: "lossOfPay" };
    selectedLeaveTypes.forEach((type) => {
      if (remainingDays <= 0) return;
      const available = type.unlimited ? remainingDays : Math.max(0, Number(stats.balances[type.value] ?? getLeaveBalance(employee, type) ?? 0));
      const allocated = Math.min(remainingDays, available);
      requestedAllocation[allocationFields[type.value]] = allocated;
      remainingDays -= allocated;
    });
    if (remainingDays > 0) {
      setMessage(`Selected balances cover only ${requestedDays - remainingDays} of ${requestedDays} day(s). Select another leave type.`);
      return;
    }

    const reasonError = getMinimumWordsError(form.reason, "Leave reason");
    if (reasonError) {
      setMessage(reasonError);
      return;
    }

    const optimisticId = `pending-${Date.now()}`;
    const optimisticLeave = {
      _id: optimisticId,
      ...form,
      leaveType: selectedLeaveTypes.length === 1 ? selectedLeaveTypes[0].value : "other",
      requestedAllocation,
      status: "pending",
      appliedOn: new Date().toISOString(),
    };
    const previousLeaves = leaves;
    const nextForm = {
      startDate: "",
      endDate: "",
      selectedLeaveTypes: [getFirstAvailableLeaveType(employee)],
      reason: "",
    };

    setLeaves((current) => {
      const nextLeaves = [optimisticLeave, ...current].slice(0, 60);
      writeLeaveCache(employee, nextLeaves);
      return nextLeaves;
    });
    setForm(nextForm);
    setApplyOpen(false);
    setSubmitting(true);
    setSubmitIndicator("submitting");
    try {
      const res = await api.post(
        "/api/employee/leave/apply",
        {
          ...form,
          leaveType: selectedLeaveTypes.length === 1 ? selectedLeaveTypes[0].value : "other",
          requestedAllocation,
        },
        { skipSaveToast: true },
      );
      setLeaves((current) => {
        const nextLeaves = current.map((leave) => leave._id === optimisticId ? res.data : leave);
        writeLeaveCache(employee, nextLeaves);
        return nextLeaves;
      });
      setSubmitIndicator("submitted");
      window.setTimeout(() => setSubmitIndicator(""), 2200);
      window.setTimeout(() => {
        fetchLeaves();
        fetchEmployee?.();
      }, 800);
    } catch (err) {
      setLeaves(previousLeaves);
      writeLeaveCache(employee, previousLeaves);
      setForm(form);
      setApplyOpen(true);
      setSubmitIndicator("failed");
      setMessage(err.response?.data?.message || "Failed to apply for leave.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="leaves-premium-wrapper text-[var(--text-primary)]">
      <main className="leave-reference-shell">

        {submitIndicator && (
          <div className={`mb-6 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold shadow-sm ${
            submitIndicator === "failed"
              ? "border-red-500/20 bg-red-500/10 text-red-600"
              : "border-emerald-500/20 bg-emerald-500/10 text-emerald-600"
          }`}>
            {submitIndicator === "submitting" ? (
              <Loader2 size={17} className="animate-spin" />
            ) : submitIndicator === "failed" ? (
              <AlertCircle size={17} />
            ) : (
              <CheckCircle2 size={17} />
            )}
            <span>
              {submitIndicator === "submitting"
                ? "Submitting leave request..."
                : submitIndicator === "failed"
                  ? "Leave request failed"
                  : "Leave request submitted"}
            </span>
          </div>
        )}

        <div className="leave-reference-layout">
          <div className="leave-reference-main">
            <section className="leaves-premium-hero">
              <div className="leaves-hero-top">
                <div className="leaves-hero-content">
                  <span className="premium-eyebrow text-[#10b981]">Leave Control</span>
                  <h1>Leave Tracker</h1>
                  <p>Manage and track your leave requests seamlessly.</p>
                  <div className="premium-meta-row mt-4">
                    <span><CalendarDays size={14} /> {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })}</span>
                    <div className="leave-month-switcher" aria-label="Leave tracker month">
                      <button
                        type="button"
                        onClick={() => setSelectedMonth((current) => addMonths(current, -1))}
                        aria-label="Previous month"
                      >
                        <ChevronLeft size={15} />
                      </button>
                      <strong>{selectedMonthLabel}</strong>
                      <button
                        type="button"
                        onClick={() => setSelectedMonth((current) => addMonths(current, 1))}
                        disabled={selectedMonthKey >= toMonthKey(new Date())}
                        aria-label="Next month"
                      >
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="leave-days-ring">
                  <strong>{stats.remainingBalance}</strong>
                  <span>days left</span>
                  <small>Total available</small>
                </div>
              </div>

              <div className="leaves-kpi-premium-grid">
                <LeaveMetric icon={<WalletCards size={19} />} label="Remaining" value={stats.remainingBalance} subLabel="Days" active />
                <LeaveMetric icon={<TimerReset size={19} />} label="Carry Forward" value={stats.carryForwardTotal} subLabel="Days" tone="info" />
              </div>
            </section>

            <section className="leaves-premium-board leave-apply-card">
              <div className="leaves-board-header">
                <div>
                  <h2>Apply Leave</h2>
                  <p>Request new leave and track status</p>
                </div>
                <button className="leaves-new-btn" type="button" onClick={() => setApplyOpen(true)}>
                  <Plus size={18} />
                  Apply Leave
                </button>
              </div>

              <div className="leave-apply-callout">
                <div className="leave-callout-icon">
                  <CalendarDays size={24} />
                </div>
                <div>
                  <strong>Ready to take a break?</strong>
                  <span>Apply for leave and get it approved seamlessly.</span>
                </div>
                <button type="button" onClick={() => setApplyOpen(true)}>
                  Apply Leave
                </button>
              </div>
            </section>

            <section className="leaves-premium-board leave-history-card">
              <div className="leaves-board-header">
                <div>
                  <h2>Leave History</h2>
                  <p>Your recent leave applications</p>
                </div>
                {leaves.length > 0 && (
                  <button
                    type="button"
                    className="leave-count-link leave-history-toggle"
                    onClick={() => {
                      setHistoryVisible((current) => !current);
                      setOpenLeaveId("");
                    }}
                  >
                    {historyVisible ? "Hide Leave History" : "View Leave History"}
                    <ChevronDown size={15} className={historyVisible ? "rotate-180" : ""} />
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-3">
                {leaves.length === 0 ? (
                  <div className="leave-empty-reference">
                    <CalendarDays size={76} />
                    <strong className="block text-[var(--text-primary)]">No leave applications yet</strong>
                    <span className="text-sm text-[var(--text-secondary)]">You haven't applied for any leave yet. Take some time off!</span>
                    <button type="button" onClick={() => setApplyOpen(true)}>Apply for Leave</button>
                  </div>
                ) : historyVisible ? (
                  leaves.map((leave) => (
                    <HistoryRow
                      key={leave._id}
                      leave={leave}
                      open={openLeaveId === leave._id}
                      onClick={() => setOpenLeaveId((current) => (current === leave._id ? "" : leave._id))}
                    />
                  ))
                ) : (
                  <div className="leave-history-private">
                    <CalendarDays size={22} />
                    <div>
                      <strong>{leaves.length} leave record{leaves.length === 1 ? "" : "s"}</strong>
                      <span>Open leave history to view application details.</span>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>

          <aside className="leave-reference-side">
            <section className="leaves-premium-board leave-side-card">
              <div className="leave-side-title">
                <span><WalletCards size={18} /></span>
                <h2>Leave Summary</h2>
              </div>

              <div className="leave-summary-grid">
                <div className="leaves-donut-inner">
                  <strong>{stats.totalAllocated}</strong>
                  <span>Total Paid Leave</span>
                </div>
                <div className="leave-summary-list">
                  <SummaryLine label="Allocated" value={`${stats.totalAllocated} day(s)`} />
                  <SummaryLine label="Left" value={`${stats.remainingBalance} day(s)`} muted />
                </div>
              </div>
            </section>

          </aside>
        </div>
      </main>

      {applyOpen && (
        <div className="leave-v3-modal-backdrop" onMouseDown={closeApplyModal}>
          <section className="leave-v3-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header className="leave-v3-modal-head">
              <div>
                <span className="premium-eyebrow text-[#10b981]">New Request</span>
                <h2>Apply for leave</h2>
              </div>
              <button type="button" onClick={closeApplyModal} aria-label="Close leave form">
                <XCircle size={20} />
              </button>
            </header>

            <form className="leave-v3-form" onSubmit={handleSubmit}>
              <div className="leave-v3-form-grid">
                <LeaveInput label="Start date">
                  <input
                    type="date"
                    required
                    min={today}
                    value={form.startDate}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        startDate: event.target.value,
                        endDate: current.endDate && current.endDate < event.target.value ? "" : current.endDate,
                      }))
                    }
                  />
                </LeaveInput>

                <LeaveInput label="End date">
                  <input
                    type="date"
                    required
                    min={form.startDate || today}
                    value={form.endDate}
                    onChange={(event) => setForm((current) => ({ ...current, endDate: event.target.value }))}
                  />
                </LeaveInput>
              </div>

              <div className="leave-v3-balance-field">
                <span className="leave-v3-balance-title">Use leave balances</span>
                <div className="leave-v3-balance-grid">
                  {requestableLeaveTypes.map((type) => {
                    const balance = type.unlimited ? null : stats.balances[type.value] ?? getLeaveBalance(employee, type);
                    const disabled = !type.unlimited && Number(balance || 0) <= 0;
                    const checked = form.selectedLeaveTypes.includes(type.value);
                    const TypeIcon = type.icon;
                    return (
                      <label
                        key={type.value}
                        className={`leave-v3-balance-option ${disabled ? "disabled" : ""} ${checked ? "selected" : ""}`}
                      >
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={checked}
                          onChange={() => setForm((current) => ({
                            ...current,
                            selectedLeaveTypes: checked
                              ? current.selectedLeaveTypes.filter((value) => value !== type.value)
                              : [...current.selectedLeaveTypes, type.value],
                          }))}
                        />
                        <span className="leave-v3-balance-icon"><TypeIcon size={18} /></span>
                        <span className="leave-v3-balance-copy">
                          <strong>{type.label}</strong>
                          <small>{type.unlimited ? "No limit" : `${balance} day(s) available`}</small>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <LeaveInput label="Reason">
                <textarea
                  required
                  rows={4}
                  value={form.reason}
                  onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
                  placeholder={`Write a clear reason for the request in at least ${MIN_TEXT_WORDS} words...`}
                />
              </LeaveInput>

              {message && (
                <div className="leave-v3-alert">
                  <AlertCircle size={17} />
                  {message}
                </div>
              )}

              <div className="leave-v3-submit">
                <span>
                  {selectedLeaveTypes.length ? selectedLeaveTypes.map((type) => type.short).join(" + ") : "Choose leave"} - {requestDateSummary}
                </span>
                <div className="leave-v3-submit-actions">
                  <button type="button" onClick={closeApplyModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting}>
                    {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                    {submitting ? "Submitting" : "Submit"}
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
};

const LeaveMetric = ({ icon, label, value, subLabel, active = false, tone = "success" }) => (
  <div className={`leaves-kpi-glass ${active ? "active" : ""} ${tone}`}>
    <span className="leave-metric-icon">{icon}</span>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      {subLabel && <small>{subLabel}</small>}
    </div>
  </div>
);

const SummaryLine = ({ label, value, muted = false }) => (
  <div className={`leave-summary-line ${muted ? "muted" : ""}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const LeaveInput = ({ label, children }) => (
  <label className="leave-v3-input">
    <span>{label}</span>
    {children}
  </label>
);

const HistoryRow = ({ leave, open, onClick }) => {
  const allocationLabels = [
    ["casualLeave", "CL"],
    ["medicalLeave", "ML"],
    ["compensatoryLeave", "CO"],
    ["lossOfPay", "LOP"],
  ].filter(([key]) => Number(leave.requestedAllocation?.[key] || 0) > 0)
    .map(([key, label]) => `${label} ${leave.requestedAllocation[key]}d`);
  const type = allocationLabels.length > 1
    ? { label: allocationLabels.join(" + ") }
    : leaveTypes.find((item) => item.value === leave.leaveType) || { label: leave.leaveType || "Leave" };
  const StatusIcon = leave.status === "approved" ? CheckCircle2 : leave.status === "rejected" ? XCircle : Clock3;

  return (
    <button type="button" className="leaves-history-item" onClick={onClick}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`flex items-center justify-center w-10 h-10 rounded-full ${leave.status === "approved" ? "bg-emerald-500/10 text-emerald-600" : leave.status === "rejected" ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-600"}`}>
            <StatusIcon size={18} />
          </span>
          <div>
            <strong className="block text-sm text-[var(--text-primary)]">{type.label}</strong>
            <span className="text-xs text-[var(--text-secondary)]">{formatDate(leave.startDate)} - {formatDate(leave.endDate)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <small className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${leave.status === "approved" ? "bg-emerald-500/10 text-emerald-600" : leave.status === "rejected" ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-600"}`}>{leave.status}</small>
          {leave.status !== "pending" && leave.processedBy && (
            <span className="text-[10px] font-bold text-[var(--text-secondary)]">
              By: {leave.processedBy.name} ({leave.processedBy.role === 'admin' ? 'Admin' : 'Team Lead'})
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-primary)] mb-2">{leave.reason}</p>
          <span className="text-xs text-[var(--text-secondary)] font-bold bg-[var(--bg-secondary)] px-2 py-1 rounded">{getLeaveDays(leave.startDate, leave.endDate)} day(s) requested</span>
          {allocationLabels.length > 0 && <span className="ml-2 text-xs font-bold text-violet-600">{allocationLabels.join(" + ")}</span>}
          {leave.rejectionReason && <em className="block mt-2 text-xs text-red-500 not-italic bg-red-500/10 px-2 py-1 rounded">Rejection note: {leave.rejectionReason}</em>}
        </div>
      )}
    </button>
  );
};

export default Leave;
